import is from '@sindresorhus/is';
import { type FunctionComponent, type ReactNode, useEffect, useRef, useState } from 'react';

import { Badge } from '~/ui/core/badge/Badge';
import { DiffTable, DiffTableRow } from '~/ui/domain/chat/DiffTable';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';

import type { Diff, EntityIdentifier, SkillChangeKind, SkillChangeRow } from '../../../data-contract/chat';
import { useSkillDetails } from '../../../skills';
import { useOpenChatSkill } from '../../components/ChatSkillSidepanel';
import { entityDiffRows, skillChangeRows } from './diff-helpers';

const SkillRowSkeleton: FunctionComponent = () => (
    <span className="inline-block h-[1.75rem] w-32 animate-pulse rounded-md bg-gray-200" />
);

const skillTypeLabel = (type: string): string => `${type.charAt(0).toUpperCase()}${type.slice(1)} skill`;

const SkillRowContent: FunctionComponent<{ readonly skillId: string; readonly struck: boolean }> = ({
    skillId,
    struck,
}) => {
    const { data, isPending } = useSkillDetails(skillId);
    const openSkill = useOpenChatSkill();
    if (isPending === true || is.nullOrUndefined(data)) return <SkillRowSkeleton />;
    return (
        <button
            type="button"
            className="flex min-w-0 cursor-pointer appearance-none flex-col gap-xxs border-0 bg-transparent p-0 text-left font-sans"
            onClick={() => openSkill(skillId)}
        >
            <span
                className={`truncate text-sm font-medium leading-tight tracking-tight ${
                    struck ? 'text-black-400 line-through' : 'text-black-900'
                }`}
            >
                {data.title}
            </span>
            <span className="truncate text-xs leading-tight text-black-400">{skillTypeLabel(data.skillType)}</span>
        </button>
    );
};

// Skill titles load lazily so a long change list does not fan out a request per
// row before the row scrolls into view.
const LazyInView: FunctionComponent<{ readonly children: ReactNode; readonly fallback: ReactNode }> = ({
    children,
    fallback,
}) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        if (isInView) return;
        const node = anchorRef.current;
        if (is.null(node)) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '120px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [isInView]);

    return (
        <div ref={anchorRef} className="flex min-w-0">
            {isInView ? children : fallback}
        </div>
    );
};

const SkillRow: FunctionComponent<{ readonly skillId: string; readonly struck: boolean }> = ({ skillId, struck }) => (
    <LazyInView fallback={<SkillRowSkeleton />}>
        <SkillRowContent skillId={skillId} struck={struck} />
    </LazyInView>
);

const EntityRowCenter: FunctionComponent<{ readonly entity: EntityIdentifier; readonly struck: boolean }> = ({
    entity,
    struck,
}) => {
    if (entity.type === 'skill' && is.nonEmptyString(entity.id)) {
        return <SkillRow skillId={entity.id} struck={struck} />;
    }
    return (
        <span
            className={`truncate text-sm font-medium leading-tight tracking-tight ${
                struck ? 'text-black-400 line-through' : 'text-black-900'
            }`}
        >
            {entity.type}
        </span>
    );
};

// TODO: legacy entities skill diff, superseded by SkillChangesView — remove later.
export const DiffEntitiesView: FunctionComponent<{ readonly diff: Diff }> = ({ diff }) => {
    const rows = entityDiffRows(diff);
    if (rows.length === 0) return null;
    return (
        <DiffTable aria-label={diff.title} columnWidths={['5.5rem', 'minmax(0,1fr)']}>
            {rows.map((row) => (
                <DiffTableRow
                    key={row.key}
                    left={
                        <Badge variant={row.change === 'add' ? 'success' : 'error'}>
                            {row.change === 'add' ? 'Add' : 'Remove'}
                        </Badge>
                    }
                    center={<EntityRowCenter entity={row.entity} struck={row.change === 'remove'} />}
                />
            ))}
        </DiffTable>
    );
};

const SKILL_CHANGE_BADGE: Record<
    SkillChangeKind,
    { readonly label: string; readonly variant: 'error' | 'info' | 'success' }
> = {
    added: { label: 'Add', variant: 'success' },
    level_changed: { label: 'Adjust', variant: 'info' },
    removed: { label: 'Remove', variant: 'error' },
};

const SkillChangeProficiency: FunctionComponent<{ readonly row: SkillChangeRow; readonly scaleCount: number }> = ({
    row,
    scaleCount,
}) => {
    // Removed skills read out the proficiency they had (greyed); added/adjusted
    // skills highlight the proposed target.
    if (row.kind === 'removed') {
        return <ProficiencyScale active={false} level={row.oldLevel} scaleCount={scaleCount} />;
    }
    return <ProficiencyScale active={true} level={row.newLevel} scaleCount={scaleCount} />;
};

export const SkillChangesView: FunctionComponent<{ readonly diff: Diff; readonly scaleCount?: number }> = ({
    diff,
    scaleCount = 5,
}) => {
    const rows = skillChangeRows(diff);
    if (rows.length === 0) return null;
    return (
        <DiffTable aria-label={diff.title}>
            {rows.map((row, index) => {
                const badge = SKILL_CHANGE_BADGE[row.kind];
                const struck = row.kind === 'removed';
                return (
                    <DiffTableRow
                        key={row.skill.id ?? `${row.kind}-${index}`}
                        left={<Badge variant={badge.variant}>{badge.label}</Badge>}
                        center={<EntityRowCenter entity={row.skill} struck={struck} />}
                        right={<SkillChangeProficiency row={row} scaleCount={scaleCount} />}
                    />
                );
            })}
        </DiffTable>
    );
};
